import { Tile } from '@/engine/tiles';
import { renderTile, tileFace } from './tiles';
import { tileName } from '@/engine/tiles';
import { GroupType } from '@/engine/decompose';

export interface TileMarker {
  type: GroupType; // 'shuntsu' | 'koutsu' | 'pair'
  color: string; // 언더라인/라벨 색(CSS)
  anchor: boolean; // 이 타일에 명칭 라벨을 붙이는지(묶음 가운데)
}

export interface HandRowCtx {
  tiles: Tile[]; // 표시 순서(13장)
  discardable: Set<Tile>; // 탭 시 버릴 수 있는 패값
  dimNonDiscardable: boolean; // 내 버림 차례에 못 버리는 패를 흐리게
  markers?: Map<number, TileMarker>; // 위치 인덱스 → 완성 묶음 마커
  recommendIndex?: number; // 추천 버림패의 표시 위치 인덱스
  onDiscard: (t: Tile) => void; // 탭(이동 없이 놓음)
  onReorder: (order: Tile[]) => void; // 드래그로 순서 변경
}

const DRAG_THRESHOLD = 8; // px — 이보다 움직이면 탭이 아닌 드래그

const GROUP_LABEL: Record<GroupType, string> = { shuntsu: '순자', koutsu: '각자', pair: '대자' };

const GROUP_TIP: Record<GroupType, string> = {
  shuntsu: '순자(順子) — 같은 무늬 숫자패 3장 연속 · 예: 2·3·4통',
  koutsu: '각자(刻子) — 똑같은 패 3장 · 예: 7삭 7삭 7삭',
  pair: "대자(対子) — 똑같은 패 2장, 손의 '머리' · 예: 東 東",
};

// 열린 설명 말풍선은 한 번에 하나. 바깥을 누르면 닫는다(캡처 단계에서 1회만 설치).
let openTip: HTMLElement | null = null;
let tipCloserReady = false;
function ensureTipCloser(): void {
  if (tipCloserReady) return;
  tipCloserReady = true;
  document.addEventListener(
    'pointerdown',
    (e) => {
      if (openTip && !openTip.contains(e.target as Node)) {
        openTip.classList.remove('open');
        openTip = null;
      }
    },
    true,
  );
}

/** 마커 라벨에 호버/클릭/키보드 설명을 배선. 라벨은 탭=버림인 타일 위에 있으므로
 *  전파를 막아 버림/드래그를 방지하고, 클릭·Enter·Space로 말풍선을 토글한다. */
function wireTip(label: HTMLElement): void {
  ensureTipCloser();
  const toggle = (): void => {
    const isOpen = label.classList.contains('open');
    if (openTip && openTip !== label) openTip.classList.remove('open');
    if (isOpen) {
      label.classList.remove('open');
      openTip = null;
    } else {
      label.classList.add('open');
      openTip = label;
    }
  };
  label.addEventListener('pointerdown', (e) => e.stopPropagation());
  label.addEventListener('click', (e) => {
    e.stopPropagation();
    toggle();
  });
  label.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    if (e.key === ' ') e.preventDefault(); // 스페이스로 페이지 스크롤 방지
    e.stopPropagation();
    toggle();
  });
}

/** 탭=버림, 드래그=정리. 포인터 이벤트 기반(모바일 터치 지원). */
export function buildHandRow(ctx: HandRowCtx): HTMLElement {
  openTip = null; // 재렌더로 이전 말풍선 DOM이 사라지므로 참조 초기화
  const row = document.createElement('div');
  row.className = 'my-hand';

  ctx.tiles.forEach((t, i) => {
    const el = renderTile(t, {
      size: 'lg',
      dim: ctx.dimNonDiscardable && !ctx.discardable.has(t),
      title: ctx.discardable.has(t) ? `${tileName(t)} · 탭: 버리기 / 드래그: 정리` : tileFace(t).label,
    });
    el.classList.add('hand-tile');
    el.dataset.tile = String(t);
    const m = ctx.markers?.get(i);
    if (m) decorateGroup(el, m);
    if (i === ctx.recommendIndex) decorateRecommend(el);
    wireTile(el, row, ctx);
    row.appendChild(el);
  });
  return row;
}

/** 추천 버림패: 상단 '추천' 배지 + 금색 테두리(CSS). */
export function decorateRecommend(el: HTMLElement): void {
  el.classList.add('tile--recommend');
  const badge = document.createElement('span');
  badge.className = 'rec-badge';
  badge.textContent = '추천';
  el.appendChild(badge);
}

/** 완성 묶음 타일에 하단 언더라인을 그리고, 대표 타일엔 명칭 라벨을 붙인다. */
function decorateGroup(el: HTMLElement, m: TileMarker): void {
  el.classList.add('tile--grp');
  el.style.setProperty('--grp', m.color);
  const bar = document.createElement('i');
  bar.className = 'grp-underline';
  el.appendChild(bar);
  if (m.anchor) {
    const label = document.createElement('span');
    label.className = 'grp-label';
    label.tabIndex = 0;
    label.setAttribute('role', 'button');
    const name = document.createElement('span');
    name.className = 'grp-label__name';
    name.textContent = GROUP_LABEL[m.type];
    label.appendChild(name);
    wireTip(label);
    el.appendChild(label);
    // 말풍선은 타일의 자식(라벨의 다음 형제)으로 둔다 → 타일 위쪽 빈 공간에 뜸
    // (라벨은 타일 아래라, 라벨 기준이면 말풍선이 타일을 덮어 잘 안 보였음).
    const tip = document.createElement('span');
    tip.className = 'grp-tip';
    tip.textContent = GROUP_TIP[m.type];
    el.appendChild(tip);
  }
}

/** 탭=버림, 드래그=정리. 드래그 동안 window에서 move/up을 듣는다(포인터 캡처에
 *  의존하지 않아 모든 브라우저·터치에서 확실히 동작). DOM은 옮기지 않고, 끄는 타일은
 *  손가락을 translateX로 따라오며 옆 패가 자리를 비킨다. 순서 확정은 놓을 때만. */
function wireTile(el: HTMLElement, row: HTMLElement, ctx: HandRowCtx): void {
  el.addEventListener('pointerdown', (down: PointerEvent) => {
    if (down.button !== undefined && down.button !== 0) return; // 좌클릭/터치만
    down.preventDefault(); // 마우스 드래그 시 텍스트 선택 방지

    const startX = down.clientX;
    const startY = down.clientY;
    let dragging = false;
    let handEls: HTMLElement[] = [];
    let slotCenters: number[] = [];
    let slotW = 0;
    let originalIndex = 0;
    let targetIndex = 0;

    const onMove = (e: PointerEvent) => {
      if (!dragging) {
        if (Math.hypot(e.clientX - startX, e.clientY - startY) < DRAG_THRESHOLD) return;
        dragging = true;
        handEls = Array.from(row.querySelectorAll<HTMLElement>('.hand-tile'));
        originalIndex = handEls.indexOf(el);
        slotCenters = handEls.map((t) => {
          const r = t.getBoundingClientRect();
          return r.left + r.width / 2;
        });
        slotW = handEls.length > 1 ? Math.abs(slotCenters[1] - slotCenters[0]) : el.offsetWidth + 4;
        el.classList.add('hand-tile--drag');
      }

      const dx = e.clientX - startX;
      el.style.transform = `translateX(${dx}px) translateY(-16px) scale(1.06)`;
      el.style.zIndex = '30';

      targetIndex = 0;
      for (let i = 0; i < handEls.length; i++) {
        if (i === originalIndex) continue;
        if (slotCenters[i] < e.clientX) targetIndex++;
      }
      handEls.forEach((t, i) => {
        if (i === originalIndex) return;
        let shift = 0;
        if (i < originalIndex && i >= targetIndex) shift = slotW;
        else if (i > originalIndex && i - 1 < targetIndex) shift = -slotW;
        t.style.transform = shift ? `translateX(${shift}px)` : '';
      });
    };

    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      if (dragging) {
        const order = handEls.map((t) => Number(t.dataset.tile));
        const [moved] = order.splice(originalIndex, 1);
        order.splice(targetIndex, 0, moved);
        handEls.forEach((t) => (t.style.transform = ''));
        el.style.zIndex = '';
        el.classList.remove('hand-tile--drag');
        ctx.onReorder(order);
      } else {
        const t = Number(el.dataset.tile);
        if (ctx.discardable.has(t)) ctx.onDiscard(t);
      }
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  });
}
